import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {HelpersService} from './helpers.service';
import {NotificationService} from './notification.service';
import {BehaviorSubject, forkJoin, Observable, Subject, throwError} from 'rxjs';
import {catchError, map, pluck} from 'rxjs/operators';
import {identity, isNil, omit, pickBy} from 'lodash-es';
import {ListViewRequestOptions, ListViewService} from './list-view/list-view-service.interface';
import {SortDirection} from '@angular/material/sort';
import {marker} from '@biesbjerg/ngx-translate-extract-marker';

@Injectable({
  providedIn: 'root',
})
export class MIHttpClientService {

  private apiUrl = environment.apiUrl;
  private apiErrorSubject: Subject<any>;
  public apiError$: Observable<any>;

  get orgId(): string {
    return localStorage.getItem('selectedOrganization');
  }

  constructor(
    private http: HttpClient,
    private helpersService: HelpersService,
    private notificationService: NotificationService,
  ) {
    this.apiErrorSubject = new BehaviorSubject<any>(null);
    this.apiError$ = this.apiErrorSubject.asObservable();
  }

  postNoJSON(relativeUrl: string, anObject?: any, options?: any, addOrgId: boolean = true): Observable<any> {
    const fullUrl = this.getFullUrl(relativeUrl, addOrgId);
    return this.postNoJsonImpl(fullUrl, anObject, options);
  }

  post(relativeUrl: string, anObject?: any, options?: any, addOrgId: boolean = true, basicErrorHandling: boolean = true): Observable<any> {
    const fullUrl = this.getFullUrl(relativeUrl, addOrgId);
    return this.postImpl(fullUrl, anObject, options, basicErrorHandling);
  }

  put(relativeUrl: string, anObject?: any, options?: any,addOrgId: boolean = true,basicErrorHandling: boolean = true) {
    const fullUrl = this.getFullUrl(relativeUrl, addOrgId);
    return this.putImpl(fullUrl, anObject, options, basicErrorHandling);
  }

  patch(relativeUrl: string, anObject?: any, options?: any, addOrgId: boolean = true, basicErrorHandling: boolean = true) {
    const fullUrl = this.getFullUrl(relativeUrl, addOrgId);
    return this.patchImpl(fullUrl, anObject, options, basicErrorHandling);
  }

  postNoErrorHandling(relativeUrl: string, anObject: any): Observable<any> {
    return this.post(relativeUrl, anObject, null, true, false);
  }


  postNoOrg(relativeUrl: string, anObject: any, basicErrorHandling: boolean = true): Observable<any> {
    const fullUrl = this.apiUrl + relativeUrl;
    return this.postImpl(fullUrl, anObject, null, basicErrorHandling);
  }

  private postImpl(fullUrl, anObject?: any, options?: any, basicErrorHandling = true): Observable<any> {
    let opts;
    if (options === undefined || options === null) {
      opts = { headers: new HttpHeaders().set('Content-Type', 'application/json;charset=utf-8') };
    } else {
      opts = options;
    }

    let ret$;
    if(!isNil(anObject)) {
      ret$ = this.http.post(fullUrl, JSON.stringify(anObject), opts);
    } else {
      ret$ = this.http.post(fullUrl, opts);
    }

    if (!basicErrorHandling) {
      return ret$;
    }

    return ret$
      .pipe(
        catchError(error => {
          this.apiErrorSubject.next(error);
          this.notificationService.error(error);
          return throwError(error);
        }),
      );
  }

  private putImpl(fullUrl, anObject?: any, options?: any, basicErrorHandling = true): Observable<any> {
    let opts;
    if (options === undefined || options === null) {
      opts = { headers: new HttpHeaders().set('Content-Type', 'application/json;charset=utf-8') };
    } else {
      opts = options;
    }

    let ret$;
    if(!isNil(anObject)) {
      ret$ = this.http.put(fullUrl, JSON.stringify(anObject), opts);
    } else {
      ret$ = this.http.put(fullUrl, opts);
    }

    if (!basicErrorHandling) {
      return ret$;
    }

    return ret$
      .pipe(
        catchError(error => {
          this.apiErrorSubject.next(error);
          this.notificationService.error(error);
          return throwError(error);
        }),
      );
  }

  private patchImpl(fullUrl, anObject?: any, options?: any, basicErrorHandling = true): Observable<any> {
    let opts;
    if (options === undefined || options === null) {
      opts = { headers: new HttpHeaders().set('Content-Type', 'application/json;charset=utf-8') };
    } else {
      opts = options;
    }

    let ret$;
    if (!isNil(anObject)) {
      ret$ = this.http.patch(fullUrl, JSON.stringify(anObject), opts);
    } else {
      ret$ = this.http.patch(fullUrl, null, opts);
    }

    if (!basicErrorHandling) {
      return ret$;
    }

    return ret$
      .pipe(
        catchError(error => {
          this.apiErrorSubject.next(error);
          this.notificationService.error(error);
          return throwError(error);
        }),
      );
  }

  private postNoJsonImpl(fullUrl, anObject?: any, options?: any): Observable<any> {
    let opts;
    if (options === undefined || options === null) {
      opts = { headers: new HttpHeaders().set('Content-Type', 'application/json;charset=utf-8') };
    } else {
      opts = options;
    }

    if(!isNil(anObject)) {
      return this.http.post(fullUrl, anObject, opts)
        .pipe(
          catchError(error => {
            this.notificationService.error(error);
            return throwError(error);
          }),
        );
    } else {
      return this.http.post(fullUrl, opts)
        .pipe(
          catchError(error => {
            this.notificationService.error(error);
            return throwError(error);
          }),
        );
    }

  }

  getListViewResults<T>(relativeUrl: string, options: ListViewRequestOptions): Observable<{ items: T[]; count: number }> {
    const params = omit(pickBy(options, identity), ['filters', 'clearCache']);

    let filterString = `${options.filters}`;
    if (relativeUrl.includes('?')) {
      filterString = filterString.replace('?', '&');
    }

    return this.get<{ list: T[]; TotalRowCount: number }>(
      relativeUrl + filterString,
      { params: new HttpParams({ fromObject: params }) },
    )
      .pipe(
        map(({ list, TotalRowCount }) => ({ items: list, count: TotalRowCount })),
      );
  }


  getAllListElements<T>(totalCount: number,
                        maxPageSize: number,
                        orderBy: string, orderDirection: SortDirection, filters: string,
                        listViewService: ListViewService<T>, skipPagination = false): Observable<T[]> {

    if(skipPagination) {
      if(totalCount > 100000) {
        this.notificationService.warn(marker("Data will be truncated to the first 100000 rows."));
      }

      const exportFilters = isNil(filters) || filters === '' ? '?skipPagination=true' : `${filters}&skipPagination=true`;
      const options = {
        pageSize: '1',
        startIndex: '1',
        orderBy: orderBy,
        orderDirection: orderDirection,
        filters: exportFilters,
      }

      return listViewService.index(options).pipe(pluck('items'));
    }

    if (totalCount / maxPageSize > 10) {
      throw new Error('Data could not be retrieved from the server.');
    }

    const requestOptions: ListViewRequestOptions[] = [];
    let remainingResults = totalCount;
    let currentPage = 0;

    const pageSize = remainingResults > maxPageSize ? maxPageSize : remainingResults;

    while (remainingResults > 0) {
      requestOptions.push({
        pageSize: `${pageSize}`,
        startIndex: `${(currentPage * pageSize + 1)}`,
        orderBy: orderBy,
        orderDirection: orderDirection,
        filters: filters,
      });

      currentPage++;
      remainingResults -= maxPageSize;
    }

    return forkJoin(
      requestOptions.map(
        (options: ListViewRequestOptions) => listViewService.index(options)
          .pipe(pluck('items')),
      ),
    ).pipe(
      map((result: T[][]) => [].concat.apply([], result)),
    );
  }

  get<T>(relativeUrl: string, options?: {
    headers?: HttpHeaders | {
      [header: string]: string | string[];
    };
    observe?: 'body';
    params?: HttpParams | {
      [param: string]: string | string[];
    };
    reportProgress?: boolean;
    responseType?: 'json';
    withCredentials?: boolean;
    timeout?: number;
  }, addOrgId: boolean = true): Observable<T> {
    const fullUrl = this.getFullUrl(relativeUrl, addOrgId);
    return this.http.get<T>(fullUrl, options)
      .pipe(
        catchError(error => {
          this.notificationService.error(error);
          return throwError(error);
        }),
      );
  }

  getNoErrorHandling<T>(relativeUrl: string, options?: {
    headers?: HttpHeaders | {
      [header: string]: string | string[];
    };
    observe?: 'body';
    params?: HttpParams | {
      [param: string]: string | string[];
    };
    reportProgress?: boolean;
    responseType?: 'json';
    withCredentials?: boolean;
    timeout?: number;
  }, addOrgId: boolean = true): Observable<T> {
    const fullUrl = this.getFullUrl(relativeUrl, addOrgId);
    return this.http.get<T>(fullUrl, options);
  }


  getText(relativeUrl: string): Observable<string>  {
    const fullUrl = this.getFullUrl(relativeUrl);
    return this.http.get(fullUrl, { responseType: 'text' }).pipe(
      catchError(error => {
        this.notificationService.error(error);
        return throwError(error);
      }),
    );
  }

  delete<T>(relativeUrl: string): Observable<T> {
    const fullUrl = this.getFullUrl(relativeUrl);
    return this.http.delete<T>(fullUrl)
      .pipe(
        catchError(error => {
          this.notificationService.error(error);
          return throwError(error);
        }),
      );
  }

  private getFullUrl(relativeUrl: string, addOrgId: boolean = true) {
    if (addOrgId) {
      return `${this.apiUrl}organizations/${this.orgId}/${relativeUrl}`;
    } else {
      return `${this.apiUrl}${relativeUrl}`;
    }
  }

}
